import { useEffect, useMemo } from 'react'
import { withAuthProvider, isImpersonating } from '../auth'
import { initTrackingContext, initTracking } from './tracking'
import { isBot } from "../Analytics/botDetection";

import { useQuery } from '@apollo/client'
import { JOB_PREFERENCES_QUERY, PGM_LEVEL_QUERY } from '../../components/queries/member'
import { COACH_TOOLS_API } from '../../apolloClient'
import { getPgmStatusLabel } from '../../components/widgets/MembershipJourney/utils/displayUtils'
import { isMembershipJourneyEligible } from '../../components/utils'

function extractUserTraits (me, membershipLevel = null, seesPathToMembershipDashboard = false, pgmTraits = {}) {
  return {
    id: me.externalId || me.id || null,
    firstName: me.firstName || null,
    lastName: me.lastName || null,
    displayName: me.displayName || null,
    class: me.class || null,
    certified: me.certified || null,
    masterProfessional: me.masterProfessional || null,
    specialized: me.specialized || null,
    type: me.type || null,
    role: me.role || null,
    phoneNumber: me.phoneNumber || null,
    primaryEmail: me.primaryEmail || null,
    loginEmail: me.loginEmail || null,
    electionDate: me.electionDate || null,
    reciprocityEligible: me.reciprocityEligible || null,
    membershipLevel: membershipLevel || null,
    seesPathToMembershipDashboard,
    mostRecentCompletedLevel: (me.education && me.education.mostRecentCompletedLevel) || null,
    educationLevelCodes: Array.isArray(me.education && me.education.levels)
      ? me.education.levels.map(level => level.levelCd).filter(Boolean)
      : null,
    terminationStatus: (me.termination && me.termination.status) || null,
    terminationType: (me.termination && me.termination.type) || null,
    terminateCd: (me.termination && me.termination.terminateCd) || null,
    terminateDate: (me.termination && me.termination.terminateDate) || null,
    sectionId: (me.section && me.section.id) || null,
    sectionName: (me.section && me.section.name) || null,
    sectionWebsite: (me.section && me.section.website) || null,
    address1: (me.address && me.address.address1) || null,
    address2: (me.address && me.address.address2) || null,
    address3: (me.address && me.address.address3) || null,
    address4: (me.address && me.address.address4) || null,
    city: (me.address && me.address.city) || null,
    state: (me.address && me.address.state) || null,
    zip: (me.address && me.address.zip) || null,
    country: (me.address && me.address.country) || null,
    membershipProgramType: pgmTraits.membershipProgramType || null,
    membershipClassification: pgmTraits.membershipClassification || null,
    pgmCurrentLevel: pgmTraits.pgmCurrentLevel || null,
    pgmCurriculumVersion: pgmTraits.pgmCurriculumVersion || null,
    pgmCareerPath: pgmTraits.pgmCareerPath || null
  }
}

const runIdentifyIfTraitsChanged = (me, membershipLevel, seesPathToMembershipDashboard, pgmTraits) => {
  if (!me.externalId && !me.id) return false

  const traits = extractUserTraits(me, membershipLevel, seesPathToMembershipDashboard, pgmTraits)
  const storedTraits = JSON.parse(localStorage.getItem('last_identified_traits') || '{}')

  const traitsChanged = JSON.stringify(traits) !== JSON.stringify(storedTraits)

  if (traitsChanged) {
    localStorage.setItem('last_identified_traits', JSON.stringify(traits))
  }

  return traitsChanged
}

const SegmentWrapper = ({ isLoggedIn, me }) => {
  const context = { api: COACH_TOOLS_API }

  const { data: jobPreferenceData } = useQuery(JOB_PREFERENCES_QUERY, {
    skip: !isLoggedIn,
    context
  })

  const { data: pgmLevelData } = useQuery(PGM_LEVEL_QUERY, {
    skip: !isLoggedIn,
    context
  })

  const jobPreference = jobPreferenceData?.me?.jobPreference
  const pgmProgressStatus = pgmLevelData?.me?.pgmProgressStatus
  const membershipLevel = getPgmStatusLabel(pgmProgressStatus)?.toLowerCase() ?? null
  const seesPathToMembershipDashboard = isMembershipJourneyEligible(
    pgmProgressStatus ? { ...me, pgmProgress: pgmProgressStatus, pgmCurriculumVersion: pgmProgressStatus.curriculumVersion } : null
  )

  const pgmTraits = useMemo(() => ({
    membershipProgramType: pgmLevelData?.me?.mainProgramTypeCode || null,
    membershipClassification: pgmLevelData?.me?.classificationCode || null,
    pgmCurrentLevel: pgmProgressStatus?.currentLevelKey || null,
    pgmCurriculumVersion: pgmProgressStatus?.curriculumVersion || null,
    pgmCareerPath: pgmProgressStatus?.careerPath || null
  }), [pgmLevelData, pgmProgressStatus])

  useEffect(() => {
    if (isBot()) return
    if (isImpersonating()) return
    
    if (typeof window === 'undefined' || !window.analytics) return
    const initTimeout = setTimeout(() => {
      try {
        const userId = me ? me.externalId || me.id : null
        if (isLoggedIn && userId) {
          const previousUserId = sessionStorage.getItem('segment_userId')
          if (runIdentifyIfTraitsChanged(me, membershipLevel, seesPathToMembershipDashboard, pgmTraits) || previousUserId !== userId) {
            window.analytics.identify(userId, {
              ...me,
              ...pgmTraits,
              membershipLevel,
              seesPathToMembershipDashboard
            });

            sessionStorage.setItem('segment_userId', userId)
          }
        } else if (!isLoggedIn && !userId) {
          sessionStorage.setItem('segment_userId', 'anonymous')
          localStorage.setItem('last_identified_traits', JSON.stringify('{}'))
          window.analytics.reset()
        }

        initTrackingContext({
          user: isLoggedIn && me ? { id: userId, ...me } : null,
          jobPreference,
          isLoggedIn
        })

        initTracking()
      } catch (error) {
        console.error('Error initializing tracking:', error)
      }
    }, 3000) // 3 second delay (login)

    return () => {
      clearTimeout(initTimeout)
    }
  }, [isLoggedIn, me, jobPreference, membershipLevel, seesPathToMembershipDashboard, pgmTraits])

  return null
}

export default withAuthProvider(SegmentWrapper)
